import React from "react";

import { Button, createStyles, IconButton, ListItem, Theme, Typography, withStyles } from "@material-ui/core";
import { observe } from "mobx";
import moment from "moment";

import { MeetingSnapshot } from "../../domain/api/meetings/MeetingSnapshot";
import { inject, observer } from "mobx-react";
import { PredefinedStoreName } from "../enums/PredefinedStoreName";
import { IContextStoreProps } from "../../stores/IContextStoreProps";
import { AppIconType } from "../shared/AppIconType";
import i18n from "../../services/localization/i18n";
import { IConfigStoreProps } from "../../stores/IConfigStoreProps";
import AppIcon from "../shared/AppIcon";
// import addonMessanger from "../workspace/AddonMessanger";
import UserAvatar from "../shared/UserAvatar";
import SnapshotSentiment from "./SnapshotSentiment";
import SnapshotType from "./SnapshotType";
import { MeetingSnapshotSentiment } from "../../domain/api/meetings/MeetingSnapshotSentiment";
import meetingSnapshotDataService from "../../services/api/MeetingSnapshotDataService";
import { KeyDownMessage } from "meet-addons-sdk";
import ConsentDialog from "../shared/ConsentDialog";

const styles = (theme: Theme) => createStyles({
    actions: {
        alignItems: "center",
        display: "flex",
        flexDirection: "row",
        height: theme.spacing(7)
    },
    button: {
        height: theme.spacing(5),
        width: theme.spacing(5),
        marginRight: theme.spacing() 
    },
    central: {
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        marginLeft: theme.spacing(0.5),
        marginRight: theme.spacing(0.5),
    },
    names: {
        display: "flex",
        flexDirection: "row",
        flexGrow: 1,
        justifyContent: "space-between"

    },
    note: {
        margin: theme.spacing(),
        width: "100%",
    },
    root: {
        color: theme.palette.text.primary,
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        padding: theme.spacing(),
    },
    row: {
        display: "flex",
        flexDirection: "row",
        flexGrow: 1,
        width: "100%"
    },
    selected: {
        height: theme.spacing(5),
        width: theme.spacing(5),
        borderColor: theme.palette.secondary.main,
        borderWidth: 2,
        borderStyle: "solid",
        marginRight: theme.spacing()
    },
    selectedNote: {
        height: theme.spacing(5),
        width: theme.spacing(5),
        borderColor: theme.palette.grey[500],
        borderWidth: 2,
        borderStyle: "solid",
        marginRight: theme.spacing()
    },
    summary: {
        alignItems: "center",
        display: "flex",
        flexDirection: "row",
        flexGrow: 1,
    },
    toolbar: {
        display: "flex",
        flexDirection: "row",
        flexGrow: 1,
        width: "100%"
    },
    toolbarItem: {
        flexGrow: 1,
    },
});

interface ISnapshotProps {
    classes: {
        actions: string;
        button: string;
        central: string;
        names: string;
        note: string;
        root: string;
        row: string;
        selected: string;
        selectedNote: string;
        summary: string;
        toolbar: string;
        toolbarItem: string;
    }
    snapshot: MeetingSnapshot;
    showAvatar: boolean;

    onSnapshotDelete: (snapshot: MeetingSnapshot) => void;
    onSnapshotLoad: (snapshot: MeetingSnapshot) => void;
    onSnapshotSelect: (snapshot: MeetingSnapshot) => void;
    selected: boolean;
}

interface ISnapshotState {
    hasNegativeSentiment: boolean;
    hasPositiveSentiment: boolean;
    sentiment?: MeetingSnapshotSentiment,
    note?: string;
    consentDialogVisible: boolean;
}

@inject(PredefinedStoreName.CONFIG)
@inject(PredefinedStoreName.CONTEXT)
@observer
class Snapshot extends React.Component<ISnapshotProps & IContextStoreProps & IConfigStoreProps, ISnapshotState> {
 
    /**
     * Creates an instance of Snapshot.
     * @param {(ISnapshotProps & IContextStoreProps & IConfigStoreProps)} props
     * @memberof Snapshot
     */
    constructor(props: ISnapshotProps & IContextStoreProps & IConfigStoreProps) {
        super(props);
        
        const { me } = this.props.configStore!;
        const sentiment = props.snapshot.sentiments.find(s => s.userId === me!.id!);

        this.state = {
            consentDialogVisible: false,
            hasNegativeSentiment: sentiment?.sentiment ? sentiment.sentiment < 50 : false,
            hasPositiveSentiment: sentiment?.sentiment ? sentiment.sentiment > 50 : false,
            note: sentiment?.note || '',
            sentiment,
        }

        observe(this.props.contextStore!, "keyDownMessage", (change) => this.handleKeyDown(change.newValue!));
    }

    public render() {

        const { classes, onSnapshotSelect, selected, showAvatar, snapshot } = this.props;
        const { snapshotUsers } =  this.props.contextStore!;
        const { note } =  this.state;

        const user = snapshotUsers.find(p => p.userId === snapshot.userId);
        if (!user) {
            
            throw new Error("No user with id:" + snapshot.userId + " found in snapshot users");
        }
        
        const longTime = moment(snapshot.timestamp).format("MMM Do HH:mm:ss");
        const shortTime = moment(snapshot.timestamp).format("hh:mm:ss");

        return <ListItem button={true} 
                         className={classes.root} 
                         selected={selected}
                         onClick={() => onSnapshotSelect(snapshot)}>
            
            <div className={classes.row}>
                { 
                    showAvatar &&  <UserAvatar user={user} />
                }

                <div className = {classes.summary}>
                    <SnapshotType snapshot={snapshot} />
                    <div className = {classes.central}>
                        <Typography>
                            { snapshot.title }
                        </Typography>
                        <Typography title={longTime}>
                            { shortTime }
                        </Typography>
                    </div>
                    <div className={classes.actions}>
                        <SnapshotSentiment snapshot={snapshot} selected={selected} />
                        <IconButton className={!!note ? classes.selectedNote : classes.button }
                                    title = { i18n.t("snapshot_sentiment_note") }>
                            <AppIcon icon={AppIconType.Note} />
                        </IconButton>
                    </div>
                </div>
            </div>
            {
                selected && this.renderNoteEditor() 
            }

            { 
                selected &&
                <div className={classes.toolbar}>
                    <Button className={classes.toolbarItem} 
                            variant="outlined"
                            style={{ marginRight: 8 }}
                            onClick={ this.showDeleteSnapshot }
                            >
                        <AppIcon icon={AppIconType.Delete} />
                        { i18n.t("snapshot_action_delete") }
                    </Button>
                    <Button className={classes.toolbarItem} 
                            variant="outlined" 
                            onClick={ this.onLoadSnapshot }>
                        <>
                            { i18n.t("snapshot_action_load") }
                        </>
                        
                    </Button>
                </div>
            }

            {
                this.state.consentDialogVisible &&

                <ConsentDialog title=  {i18n.t("snapshot_delete_dialog_title")}
                    description=    {i18n.t("snapshot_delete_dialog_description")}
                    onAccept={ this.onDeleteSnapshot }
                    onCancel={() => this.setState({
                        consentDialogVisible: false,
                    })}
                    open={true} />
            }

        </ListItem>
    }

    private showDeleteSnapshot = () => {
        this.setState({
            consentDialogVisible: true,
        });
    }

    private onLoadSnapshot = () => {
        const { snapshot } = this.props;
        this.props.onSnapshotLoad(snapshot);
    }

    private onDeleteSnapshot = async () => {
        const { snapshot } = this.props;

        this.setState({
            consentDialogVisible: false,
        }, );

        this.props.onSnapshotDelete(snapshot);
    }

    private renderNoteEditor = () => {
        const { note } = this.state;
        const { classes } = this.props;
        return <textarea className={classes.note}
                         autoFocus={true}
                         value={note} 
                         rows={3}
                         onKeyDown={this.handleAreaKeyDown }
                         onChange={e => this.setState({ note: e.target.value})}
                         onBlur={this.publishSentimentNote}
                         />
    }


    private publishSentimentNote = () => {
        const { id, partitionKey } = this.props.snapshot;
        const { me } = this.props.configStore!;
        const { addOrUpdateSnapshot } = this.props.contextStore!;

        if (this.state.sentiment?.note === this.state.note) {
            return;
        }

        const sentiment = {
            userId: me!.id,
            partitionKey, 
            sentiment: this.state.sentiment?.sentiment,
            note: this.state.note
        } as MeetingSnapshotSentiment;

        meetingSnapshotDataService.recordSentimentAsync(id!, sentiment)
            .then(snapshot => {
                addOrUpdateSnapshot(snapshot);
            });
    }

    private handleAreaKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        switch (e.keyCode) {
            case 107: // num +
            case 187: // +
            case 109: // num -
            case 189: // -
                e.preventDefault();
                return false;
        }

        return;
    }

    private handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement | HTMLTextAreaElement> | KeyDownMessage) => {
        switch (e.keyCode) {
            case 27: // esc
                this.publishSentimentNote();
                break;
        }
    }
}

export default withStyles(styles)(Snapshot);